// DEV-2684 — "Kişiye özel yetkiler" modalının SAF mantığı (bileşenden ayrık, birim test edilebilir).
//
// Kural: ekranda gösterilen değer etkin hücredir (rol + kişi satırı). Kayıtta role eşit alan `null` gider
// ("rolden gelsin"), böylece rol sonradan değişirse eşit alanlar rolü izlemeye devam eder. Kişiye özel olan,
// saklanacak kişi satırında dolu alandır (kisiHucresiNormalle); değiştirilen satırda bu rolden farklı alandır.
import {KADEME_SECENEKLERI, YetkiKademe} from "@/enum/YetkiModulleri";

export type Bayrak = "ekle" | "degistir" | "sil";
export type OzelAlan = "kademe" | Bayrak;

export const BAYRAKLAR: Bayrak[] = ["ekle", "degistir", "sil"];
export const OZEL_ALANLAR: OzelAlan[] = ["kademe", ...BAYRAKLAR];

/** Bir modülün (rol, kişi ya da etkin) hücre değeri. */
export interface Hucre {
  kademe: YetkiKademe;
  ekle: boolean;
  degistir: boolean;
  sil: boolean;
}

/** PUT gövdesindeki tek satırın alanları; null = rolden gelsin. */
export interface SatirPayload {
  kademe: YetkiKademe | null;
  ekle: boolean | null;
  degistir: boolean | null;
  sil: boolean | null;
}

/**
 * Sunucu hücresini ekran değerine çevirir. İşlem bayrakları yalnız Düzenle kademesinde anlamlıdır
 * (matris ekranı da başka kademede kapatır); başka kademede false sayılır.
 */
export function hucreNormalle(ham: any): Hucre {
  const kademe = ham && ham.kademe ? (ham.kademe as YetkiKademe) : YetkiKademe.GIZLI;
  const duzenle = kademe === YetkiKademe.DUZENLE;
  return {
    kademe,
    ekle: duzenle && !!(ham && ham.ekle),
    degistir: duzenle && !!(ham && ham.degistir),
    sil: duzenle && !!(ham && ham.sil),
  };
}

/** Alan, rol hücresinden farklı mı? Bayraklar yalnız ekranda Düzenle iken karşılaştırılır. */
export function alanOzelMi(deger: Hucre, rol: Hucre, alan: OzelAlan): boolean {
  if (alan === "kademe") {
    return deger.kademe !== rol.kademe;
  }
  return deger.kademe === YetkiKademe.DUZENLE && deger[alan] !== rol[alan];
}

/** Satırda rolden farklı olan alanlar. */
export function ozelAlanlar(deger: Hucre, rol: Hucre): OzelAlan[] {
  return OZEL_ALANLAR.filter((alan) => alanOzelMi(deger, rol, alan));
}

/** Satırda en az bir alan özel mi? */
export function satirOzelMi(deger: Hucre, rol: Hucre): boolean {
  return ozelAlanlar(deger, rol).length > 0;
}

/** Satırın açılıştaki değerinden farkı var mı? (yalnız değişen satırlar PUT'a gider) */
export function satirDegistiMi(simdi: Hucre, ilk: Hucre): boolean {
  return simdi.kademe !== ilk.kademe
    || simdi.ekle !== ilk.ekle
    || simdi.degistir !== ilk.degistir
    || simdi.sil !== ilk.sil;
}

/** "Rolden gelsin": satırın tüm alanları rol değerine döner. */
export function roldenGelsin(rol: Hucre): Hucre {
  return {...rol};
}

/**
 * Kademe değişince bayrakları matristeki gibi ayarlar: Düzenle dışında hepsi kapanır; Düzenle'ye
 * geçişte (bayrak yoksa) rolün bayrakları geri gelir, böylece rolün kademesine dönmek rol hücresini
 * birebir verir.
 */
export function kademeSonrasiHucre(deger: Hucre, rol: Hucre): Hucre {
  if (deger.kademe !== YetkiKademe.DUZENLE) {
    return {...deger, ekle: false, degistir: false, sil: false};
  }
  if (!deger.ekle && !deger.degistir && !deger.sil) {
    return {...deger, ekle: rol.ekle, degistir: rol.degistir, sil: rol.sil};
  }
  return {...deger};
}

/**
 * PUT satırı: role eşit alan null. Düzenle dışı kademede bayraklar null gider (sunucu o durumda
 * true'yu 422 ile reddeder, gizli kademede hiçbir bayrağı kabul etmez).
 */
export function satirPayload(deger: Hucre, rol: Hucre): SatirPayload {
  const duzenle = deger.kademe === YetkiKademe.DUZENLE;
  const bayrak = (alan: Bayrak): boolean | null =>
    duzenle && alanOzelMi(deger, rol, alan) ? deger[alan] : null;
  return {
    kademe: alanOzelMi(deger, rol, "kademe") ? deger.kademe : null,
    ekle: bayrak("ekle"),
    degistir: bayrak("degistir"),
    sil: bayrak("sil"),
  };
}

/**
 * Sunucunun saklı kişi satırı (`kisi_hucresi`): null alan rolden gelir, satır yoksa tüm alanlar null. Ekran değeri
 * (etkin hücre) rolle aynı olsa da dolu alan saklıdır: rol sonradan değişince devreye girer.
 */
export function kisiHucresiNormalle(ham: any): SatirPayload {
  const deger = (alan: OzelAlan): any => (ham && ham[alan] !== undefined && ham[alan] !== null ? ham[alan] : null);
  const bayrak = (alan: Bayrak): boolean | null => (deger(alan) === null ? null : !!deger(alan));
  return {kademe: deger("kademe"), ekle: bayrak("ekle"), degistir: bayrak("degistir"), sil: bayrak("sil")};
}

/** Satır kişiye özel değer taşıyor mu: en az bir alanı dolu (sunucunun `ozel_yetki_sayisi` ölçütü). */
export function kisiDegeriVarMi(saklanan: SatirPayload): boolean {
  return OZEL_ALANLAR.some((alan) => saklanan[alan] !== null);
}

/** İki saklı satır alan alan aynı mı (null ile false farklıdır). */
export function saklananAyniMi(a: SatirPayload, b: SatirPayload): boolean {
  return OZEL_ALANLAR.every((alan) => a[alan] === b[alan]);
}

/** Kademe değerinin ekran etiketi ("Görünür-Kapalı" vb.). */
export function kademeEtiketi(kademe: string): string {
  const secenek = KADEME_SECENEKLERI.find((k) => k.value === kademe);
  return secenek ? secenek.label : String(kademe);
}

/**
 * Kişiye özel simgesinin ipucu: saklı değer rolden farklıysa rolün değerini söyler; rolle aynıysa ekranda fark görünmez,
 * ipucu neden özel olduğunu (rol değişince korunur) söyler.
 */
export function ozelIpucu(alan: OzelAlan, rol: Hucre, saklanan: SatirPayload): string {
  const rolDegeri = alan === "kademe" ? kademeEtiketi(rol.kademe) : (rol[alan] ? "Açık" : "Kapalı");
  if (saklanan[alan] === rol[alan]) {
    return `Bu kullanıcıya özel · rolle aynı (${rolDegeri}); rol değişirse korunur`;
  }
  return `Bu kullanıcıya özel · rolde: ${rolDegeri}`;
}

const BAYRAK_ETIKETLERI: { [alan in Bayrak]: string } = {ekle: "Ekle", degistir: "Değiştir", sil: "Sil"};

/**
 * Düzenle dışı kademede saklı bayrak ekranda görünmez (işlem düğmeleri kapalı); satır işaretinin ipucu, rol Düzenle olunca
 * devreye girecek değerleri sayar. Saklı bayrak yoksa boş.
 */
export function gizliBayrakIpucu(saklanan: SatirPayload): string {
  const parcalar = BAYRAKLAR
      .filter((alan) => saklanan[alan] !== null)
      .map((alan) => `${BAYRAK_ETIKETLERI[alan]} ${saklanan[alan] ? "açık" : "kapalı"}`);
  return parcalar.length > 0 ? `Bu kullanıcıya özel · Rol Düzenle olursa: ${parcalar.join(", ")}` : "";
}
